// src/pages/Appli/AppliAdmin/Pages/Partenaires/CreatePartenairesModale.jsx
import React, { useMemo, useRef, useState } from "react";
import Button from "../../../../components/Button/Button";
import { uploadImage } from "../../../../services/partenairesServices";
import { FaSave, FaTimes, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const toAbs = (url) => {
  if (!url) return "";
  return isExternalURL(url) ? url : `${BASE_URL}${url}`;
};

const CreatePartenairesModale = ({ onCreate, onClose }) => {
  const [nom, setNom] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [description, setDescription] = useState("");
  const [ordre, setOrdre] = useState(1);
  const [isActive, setIsActive] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const fileRef = useRef(null);

  const previewUrl = useMemo(() => {
    if (imageFile) return URL.createObjectURL(imageFile);
    if (photoUrl) return toAbs(photoUrl);
    return "";
  }, [imageFile, photoUrl]);

  const clearImage = () => {
    setImageFile(null);
    setPhotoUrl("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleCreate = async () => {
    if (!nom.trim()) {
      toast.error("Le nom est obligatoire.");
      return;
    }

    setIsLoading(true);

    let uploadedImageUrl = photoUrl;

    try {
      if (imageFile) {
        const fd = new FormData();
        fd.append("image", imageFile);
        fd.append("category", "partenaires");

        const uploadResponse = await uploadImage(fd);
        if (!uploadResponse?.filePath) throw new Error("Réponse upload invalide");
        uploadedImageUrl = uploadResponse.filePath;
      }

      const payload = {
        nom: nom.trim(),
        photo_url: String(uploadedImageUrl || "").trim(),
        link_url: String(linkUrl || "").trim(),
        description: String(description || "").trim(),
        ordre: ordre !== "" ? Number(ordre) : 1,
        is_active: !!isActive,
      };

      await onCreate(payload);
      onClose();
    } catch (error) {
      console.error("Erreur création partenaire :", error);
      toast.error("Erreur lors de la création du partenaire.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden border border-white/10">
        <div className="bg-primary text-white p-4 flex items-center justify-between">
          <h2 className="text-xl font-extrabold">Créer un partenaire</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl px-3 py-2 bg-white/15 hover:bg-white/20 transition"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
          {/* Nom */}
          <div>
            <label className="block text-sm font-semibold mb-2 dark:text-gray-200">
              Nom *
            </label>
            <input
              type="text"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              className="w-full rounded-2xl p-3 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Logo */}
          <div className="rounded-3xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20 p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-extrabold dark:text-gray-100">
                Logo
              </div>
              {(imageFile || photoUrl) && (
                <button
                  type="button"
                  onClick={clearImage}
                  className="inline-flex items-center gap-2 text-xs font-bold text-red-600 hover:text-red-700"
                >
                  <FaTrash /> Supprimer
                </button>
              )}
            </div>

            {previewUrl ? (
              <div className="mt-3 w-full h-28 rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/30 flex items-center justify-center overflow-hidden">
                <img src={previewUrl} alt="preview" className="max-h-24 object-contain" />
              </div>
            ) : (
              <div className="mt-3 text-xs text-gray-600 dark:text-gray-300">
                Aucun logo sélectionné.
              </div>
            )}

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-1 dark:text-gray-200">
                  Télécharger une image
                </label>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                  className="w-full rounded-2xl p-2 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1 dark:text-gray-200">
                  URL (optionnel)
                </label>
                <input
                  type="text"
                  value={photoUrl}
                  onChange={(e) => setPhotoUrl(e.target.value)}
                  className="w-full rounded-2xl p-2 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  placeholder="https://..."
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Lien */}
          <div>
            <label className="block text-sm font-semibold mb-2 dark:text-gray-200">
              Lien
            </label>
            <input
              type="text"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className="w-full rounded-2xl p-3 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              placeholder="https://..."
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold mb-2 dark:text-gray-200">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-2xl p-3 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 min-h-[96px]"
            />
          </div>

          {/* Ordre + Actif */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-2 dark:text-gray-200">
                Ordre
              </label>
              <input
                type="number"
                value={ordre}
                onChange={(e) => setOrdre(e.target.value)}
                className="w-full rounded-2xl p-3 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>

            <div className="flex items-center gap-3 mt-7">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-5 w-5 accent-primary"
              />
              <span className="text-sm font-semibold dark:text-gray-200">
                Partenaire actif
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-black/10 dark:border-white/10 flex justify-end gap-2">
          <Button
            text="Annuler"
            onClick={onClose}
            disabled={isLoading}
            icon={FaTimes}
            className="gap-2"
          />
          <Button
            text="Créer"
            onClick={handleCreate}
            disabled={isLoading}
            icon={FaSave}
            className="gap-2"
          />
        </div>
      </div>
    </div>
  );
};

export default CreatePartenairesModale;
